import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Clock, Coffee, ShoppingBag, Droplets, ShieldCheck } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import Marquee from "../components/Marquee";
import SEO from "../components/SEO";

const IMG_HERO = "https://images.pexels.com/photos/3493047/pexels-photo-3493047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const IMG_TEXTILE = "https://images.unsplash.com/photo-1711605536037-eb9c2b42235c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000&h=1200&fit=crop";
const IMG_MUGS = "https://images.unsplash.com/photo-1573317004357-91bfead79b7c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000&h=1200&fit=crop";

function ParallaxImg({ src, className }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <div ref={ref} className={`overflow-hidden border border-line ${className}`}>
      <motion.img style={{ y, scale: 1.2 }} src={src} alt="" className="h-full w-full object-cover" />
    </div>
  );
}

const STEPS = [
  { n: "01", title: "Votre visuel", text: "Vous nous envoyez votre photo, votre texte ou votre motif en couleur. On prépare le fichier en haute définition." },
  { n: "02", title: "L'impression", text: "Le motif est imprimé sur un papier transfert avec des encres spéciales dites « à sublimation »." },
  { n: "03", title: "Le transfert", text: "Sous l'effet de la chaleur (environ 200°C) et de la pression, l'encre se transforme en gaz et pénètre la matière : les couleurs deviennent partie intégrante de l'objet." },
  { n: "04", title: "La finition", text: "Le rendu est éclatant, précis et durable — il ne s'écaille pas et résiste au lavage (et au lave-vaisselle pour les mugs)." },
];

const SUPPORTS = [
  { icon: Coffee, title: "Céramique & verre", text: "Mugs et planches en verre : idéals pour une photo souvenir ou un dégradé de couleurs qui claque." },
  { icon: Droplets, title: "Métal & alu", text: "Gourdes isothermes et boîtes en aluminium : un motif couleur qui habille tout l'objet." },
  { icon: ShoppingBag, title: "Textile", text: "Tote bags en toile : votre visuel imprimé, souple et léger, à porter au quotidien." },
];

export default function Sublimation() {
  return (
    <div>
      <SEO title="Sublimation — couleurs dans la matière" path="/sublimation" image={IMG_HERO}
        description="Sublimation en Suisse : vos photos et motifs imprimés en couleur dans la matière — mugs, gourdes, tote bags. Rendu éclatant qui ne s'écaille pas." />
      <section className="mx-auto max-w-[1400px] px-5 pt-24 pb-16 md:px-10">
        <p className="mb-4 text-xs uppercase tracking-[0.3em] text-terra">Savoir-faire</p>
        <h1 className="max-w-4xl font-serif text-5xl font-light leading-[0.95] tracking-tight text-ink md:text-8xl">
          <MaskedLines lines={["La", "sublimation."]} />
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-ink2">
            La sublimation imprime vos <strong className="text-ink">couleurs</strong> directement dans la matière.
            Photos, dégradés, motifs complexes : le rendu est <strong className="text-ink">éclatant</strong> et ne
            s&apos;écaille pas. Parfait pour offrir un mug, une gourde ou un tote bag entièrement à votre image.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10">
        <ParallaxImg src={IMG_HERO} className="aspect-[16/9] w-full" />
      </section>

      {/* Comment ça marche */}
      <section className="mx-auto max-w-[1400px] px-5 pb-8 md:px-10">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-terra">Comment ça marche</p>
          <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight text-ink md:text-6xl">
            La couleur, fixée dans la matière.
          </h2>
        </Reveal>
        <div className="mt-12 space-y-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.06} className="flex items-start gap-5 border-t border-line pt-6 md:gap-8">
              <span className="w-12 shrink-0 font-serif text-3xl font-light text-terra md:text-4xl">{s.n}</span>
              <div>
                <h3 className="font-serif text-xl font-medium text-ink md:text-2xl">{s.title}</h3>
                <p className="mt-1 max-w-2xl leading-relaxed text-ink2">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-4 px-5 py-24 md:grid-cols-12 md:px-10">
        <ParallaxImg src={IMG_MUGS} className="aspect-[4/5] md:col-span-6" />
        <ParallaxImg src={IMG_TEXTILE} className="aspect-[4/5] md:col-span-6" />
      </section>

      {/* Les supports */}
      <section className="mx-auto max-w-[1400px] px-5 pb-8 md:px-10">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-terra">Les supports</p>
          <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight text-ink md:text-6xl">
            Sur quoi on sublime.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {SUPPORTS.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.1} className="border border-line bg-sand/40 p-8 md:p-10">
              <m.icon className="h-8 w-8 text-terra" strokeWidth={1.5} />
              <h3 className="mt-6 font-serif text-2xl font-medium text-ink">{m.title}</h3>
              <p className="mt-3 leading-relaxed text-ink2">{m.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={["Sublimation", "Couleurs Éclatantes", "Photo & Motifs", "Fait Maison", "Pièce Unique"]} />

      {/* Délais */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10">
        <Reveal className="flex flex-col gap-8 border border-line bg-sand/40 p-8 md:flex-row md:items-center md:justify-between md:p-12">
          <div className="flex items-start gap-4">
            <Clock className="mt-1 h-7 w-7 shrink-0 text-terra" strokeWidth={1.5} />
            <div>
              <h3 className="font-serif text-2xl font-medium text-ink">Délais indicatifs</h3>
              <p className="mt-2 max-w-xl leading-relaxed text-ink2">
                Comptez <strong className="text-ink">3 à 7 jours ouvrés</strong> pour une pièce personnalisée,
                selon le visuel. Les produits déjà réalisés partent sous 48h.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <ShieldCheck className="mt-1 h-7 w-7 shrink-0 text-terra" strokeWidth={1.5} />
            <div>
              <h3 className="font-serif text-2xl font-medium text-ink">Garantie fait maison</h3>
              <p className="mt-2 max-w-xs leading-relaxed text-ink2">
                Chaque pièce est contrôlée à la main avant l&apos;envoi.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-5 pb-32 md:px-10">
        <Reveal className="border border-line bg-ink p-10 text-cream md:p-20">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
            <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight md:text-6xl">
              Personnaliser un objet.
            </h2>
            <Link
              to="/catalogue"
              data-testid="sublimation-cta"
              className="group flex shrink-0 items-center gap-2 border border-cream px-8 py-4 text-sm uppercase tracking-widest transition-colors hover:bg-cream hover:text-ink active:scale-95"
            >
              Ouvrir le catalogue
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
